export type InlineSaveScope = 'environment' | 'project';

export type InlineSaveRow = {
  rowKey: string;
  id?: number;
  tempId?: string;
  key: string;
  value: string;
  scope: InlineSaveScope;
  originalScope?: InlineSaveScope;
  description?: string;
};

export type InlineSaveAction = 'skip' | 'delete' | 'create' | 'update';

/** Decide create vs update from backend id + key. Empty value must not block an update. */
export function resolveInlineVariableSaveAction(row: InlineSaveRow): InlineSaveAction {
  const keyTrim = (row.key ?? '').trim();
  if (!keyTrim) {
    if (row.id != null) {
      const emptyRest = !(row.value ?? '').trim() && !(row.description ?? '').trim();
      return emptyRest ? 'delete' : 'skip';
    }
    return 'skip';
  }
  return row.id != null ? 'update' : 'create';
}

/** Merge CREATE response id into the same UI row. Never append a second row. */
export function mergeCreatedIdIntoRows<T extends { rowKey: string; id?: number; tempId?: string }>(
  rows: T[],
  rowKey: string,
  createdId: number,
  options?: { replaceExisting?: boolean }
): T[] {
  return rows.map((row) => {
    if (row.rowKey !== rowKey) return row;
    if (row.id != null && !options?.replaceExisting) return row;
    return { ...row, id: createdId, tempId: undefined };
  });
}

export function bumpSaveVersion(versions: Record<string, number>, rowKey: string): number {
  const next = (versions[rowKey] ?? 0) + 1;
  versions[rowKey] = next;
  return next;
}

export function isCurrentSaveVersion(
  versions: Record<string, number>,
  rowKey: string,
  version: number
): boolean {
  return versions[rowKey] === version;
}

/**
 * When a newer save is scheduled, ignore the outdated response except for id merge.
 */
export function shouldApplySaveResult(
  versions: Record<string, number>,
  rowKey: string,
  version: number
): boolean {
  return isCurrentSaveVersion(versions, rowKey, version);
}
